/**
 * tradeMatchModel — Push 5 / v1.4.4 b6.
 *
 * Pure, DB-free selection logic for the Trade Match surface. Kept separate
 * from TradeMatchCard.tsx so the data-integrity invariants can be unit-tested
 * without a React renderer:
 *
 *   - rows come straight from the live /api/trade/candidates payload,
 *   - the hero (You give / You get) pair requires BOTH sides populated from
 *     real species ids — never a placeholder,
 *   - match-quality badges are derived from the real candidate score band,
 *   - there is no demo/sample fallback path here at all.
 */
import { isVariantAvailable, isTradableSpecies } from "./variantAvailability";
import { orientCompletedTrade } from "./tradeHistoryOrientation";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/** The fields of a proposal this module needs — see proposedSideKeys. */
type ProposalSides = Parameters<typeof orientCompletedTrade>[0];

// PR-E-cleanup: mirror the backend `TradeCategory` union in
// `cloudflare/src/lib/tradeMatching.ts`. The matcher emits `purified_swap` for
// equal-variant purified-for-purified pairings (see tradeMatching.ts line 219+)
// and the UI (TradeMatchCard CAT_LABEL / VARIANT_CARD_TONE / hero tone) already
// handles it — the type was the one place that omitted it.
export type TradeMatchCategory =
  | "shiny_for_shiny"
  | "purified_swap"
  | "lucky_candidate"
  | "regional_swap"
  | "dex_filler_outbound"
  | "dex_filler_inbound"
  | "lucky_trade_ready";

export interface TradeCandidateRow {
  id: number;
  friendAccountId: number;
  friendName: string;
  category: TradeMatchCategory;
  ownerSpeciesId: number | null;
  ownerFormId: string | null;
  friendSpeciesId: number | null;
  friendFormId: string | null;
  score: number;
  plannedAt: string | null;
  planNote: string | null;
  rationaleJson: string | null;
  // Historic: rows the (now read-only) unlock ledger pinned. The server orders
  // these first inside the visible set, so the flag no longer drives the blur.
  unlocked?: boolean;
}

/**
 * Trades a Basic user sees unmasked per friend. Mirrors
 * FREE_TRADES_VISIBLE_PER_FRIEND in cloudflare/src/lib/unlockPolicy.ts — used
 * only as the fallback when a response predates the `visibleCount` field. The
 * server value wins.
 */
export const FREE_TRADES_VISIBLE_PER_FRIEND = 3;

export interface TradeCandidatesResponse {
  locked: boolean;
  items?: TradeCandidateRow[];
  preview?: TradeCandidateRow[];
  upsell?: string;
  // How many leading preview rows are unmasked for this tier. Present only for
  // a Basic user scoped to a specific friend; absent for Pro and the global
  // preview (which is capped by row count instead).
  visibleCount?: number;
}

/** Rows to render: the unlocked list, or the free-tier preview when locked. */
export function selectRows(data: TradeCandidatesResponse | undefined): TradeCandidateRow[] {
  if (!data) return [];
  const raw = data.locked ? data.preview ?? [] : data.items ?? [];
  return raw.filter(isTradeableRow);
}

/**
 * How many leading rows render unmasked, and whether row `i` is a blurred tease.
 *
 * Position decides it, not the row's `unlocked` flag: the server orders any
 * ledger-earned rows first, so they always land inside the visible set. An
 * unlocked flag on a row past the cap would otherwise re-open a gate the server
 * intended to close.
 *
 * Pro (`locked: false`) never masks anything.
 */
export function visibleRowCount(data: TradeCandidatesResponse | undefined): number {
  return data?.visibleCount ?? FREE_TRADES_VISIBLE_PER_FRIEND;
}

export function isRowMasked(index: number, locked: boolean, visibleCount: number): boolean {
  return locked && index >= visibleCount;
}

/**
 * The Pokédex variant a trade category is denominated in, so availability can
 * be checked against the right per-variant roster (1.8.7 Task 4).
 */
export function variantForTradeCategory(category: TradeMatchCategory): PokedexVariant {
  switch (category) {
    case "shiny_for_shiny":
      return "shiny";
    case "purified_swap":
      return "purified";
    case "lucky_candidate":
    case "lucky_trade_ready":
      return "lucky";
    // regional_swap / dex_filler_inbound / dex_filler_outbound are base-species.
    default:
      return "pokemon";
  }
}

/**
 * A trade row is only valid when every populated species on it can actually
 * exist in Pokémon GO for the category's variant. A recommendation for a
 * species/variant that GO can't produce (e.g. a shiny that isn't released) is
 * invalid and must never surface — mirrors the missing-list filter so the two
 * surfaces agree on availability.
 *
 * 2026-08-07: existing is necessary but not sufficient — every row here is a
 * proposed trade, so both sides must also be TRADABLE. A base-variant category
 * (dex filler, regional swap) passed the availability check for a Mythical,
 * since the species IS in the base roster; it just can't change hands. The
 * worker's scorer applies the same gate before these rows are ever emitted.
 */
export function isTradeableRow(row: TradeCandidateRow): boolean {
  const variant = variantForTradeCategory(row.category);
  if (row.ownerSpeciesId != null && !isTradableSpecies(row.ownerSpeciesId)) return false;
  if (row.friendSpeciesId != null && !isTradableSpecies(row.friendSpeciesId)) return false;
  if (row.ownerSpeciesId != null && !isVariantAvailable(variant, row.ownerSpeciesId)) return false;
  if (row.friendSpeciesId != null && !isVariantAvailable(variant, row.friendSpeciesId)) return false;
  return true;
}

/**
 * The hero pair is the first real candidate with BOTH sides populated. We do
 * not synthesise one from a single-sided row, so the give/get cards only ever
 * draw real species.
 */
export function selectHero(rows: TradeCandidateRow[]): TradeCandidateRow | null {
  return rows.find((r) => r.ownerSpeciesId != null && r.friendSpeciesId != null) ?? null;
}

// ── Trade-list-driven highlight (Compare "Trade Match" block) ────────────────
// The Trade Match hero is no longer picked by the /api/trade/candidates ranker.
// It is the single most-suitable swap built from the user's OWN trade list — the
// give/get trades they earmarked below — with a fallback to the first surfaced
// give + get lead when the list is empty. Pure so it can be unit-tested.

/** One side of a swap: the species and the dex variant that line is about. */
export interface TradeSide {
  speciesId: number;
  variant: string;
}

/** The highlighted swap: what you give and what you get. */
export interface TradeHighlight {
  give: TradeSide;
  get: TradeSide;
}

/**
 * Pick the most suitable give+get pairing for the Trade Match highlight.
 *
 * Candidates: the earmarked trades when present (all planned gives × all planned
 * gets), else a single fallback pair from the first surfaced give + get lead.
 * The pairing is scored so that:
 *   - same-variant swaps are preferred (a Shiny-for-Shiny reads cleaner than a
 *     Shiny-for-base), and
 *   - Lucky is a last resort by default (only surfaced when nothing else pairs),
 *     BUT is promoted to first choice when the trainer has flagged a Lucky friend
 *     (every trade comes back Lucky, so a Lucky lead is the point).
 * Ties break toward the earliest-listed give, then get, so "the first listed
 * give + first listed get" wins when scores are equal.
 *
 * Returns null when either side has no candidate (nothing to pair yet).
 */
export function selectTradeMatchHighlight(
  plannedGives: readonly TradeSide[],
  plannedGets: readonly TradeSide[],
  fallbackGive: TradeSide | null,
  fallbackGet: TradeSide | null,
  luckyFriend: boolean,
): TradeHighlight | null {
  const gives = plannedGives.length > 0 ? plannedGives : fallbackGive ? [fallbackGive] : [];
  const gets = plannedGets.length > 0 ? plannedGets : fallbackGet ? [fallbackGet] : [];
  if (gives.length === 0 || gets.length === 0) return null;

  let best: { give: TradeSide; get: TradeSide; score: number; order: number } | null = null;
  for (let gi = 0; gi < gives.length; gi++) {
    for (let ti = 0; ti < gets.length; ti++) {
      const give = gives[gi];
      const get = gets[ti];
      const sameVariant = give.variant === get.variant;
      const isLucky = give.variant === "lucky" || get.variant === "lucky";
      // Lower score is better. Lucky flips from a +2 penalty to a 0 bonus when
      // the friend is Lucky; same-variant is always the finer tiebreak.
      const score = luckyFriend
        ? (isLucky ? 0 : 2) + (sameVariant ? 0 : 1)
        : (isLucky ? 2 : 0) + (sameVariant ? 0 : 1);
      const order = gi * gets.length + ti;
      if (!best || score < best.score || (score === best.score && order < best.order)) {
        best = { give, get, score, order };
      }
    }
  }
  return best ? { give: best.give, get: best.get } : null;
}

/** The identity of one side of a swap: a species in a particular dex variant. */
export function tradeSideKey(s: TradeSide): string {
  return `${s.variant}:${s.speciesId}`;
}

/**
 * The copies already committed to a proposal that is still in flight, as
 * `tradeSideKey`s, split by which way they are travelling FOR THE READER.
 *
 * The Trade Match card skips any pairing that touches one of these, so asking
 * for a swap rolls the card on to the next one instead of re-offering what is
 * already on the table. Either side is enough to disqualify a pairing: a trainer
 * has one of the thing they offered, and pledging it to a second trade while the
 * first is unanswered is a promise they cannot keep.
 *
 * ORIENTATION IS THE WHOLE JOB. A proposal is one shared row written from the
 * PROPOSER's side, and the endpoint returns it to both trainers, so a proposal
 * the friend sent has its give and get the other way round from the reader's
 * point of view. Unoriented, it would block the wrong half of the reader's list
 * — and keep offering the very swap they had just been asked for.
 *
 * Pass only proposals that are actually live: a declined or cancelled one
 * releases its copies, and this function trusts what it is given.
 */
export function proposedSideKeys(
  liveProposals: readonly ProposalSides[],
  friendId: number,
): { gives: Set<string>; gets: Set<string> } {
  const gives = new Set<string>();
  const gets = new Set<string>();
  for (const p of liveProposals) {
    const o = orientCompletedTrade(p, friendId);
    gives.add(tradeSideKey({ speciesId: o.gaveId, variant: o.gaveVariant }));
    gets.add(tradeSideKey({ speciesId: o.gotId, variant: o.gotVariant }));
  }
  return { gives, gets };
}

/** Map a real candidate score to the badge bands shown in the reference. */
export function matchBadge(score: number): { label: string; strong: boolean } {
  if (score >= 80) return { label: "Best match", strong: true };
  if (score >= 50) return { label: "Good", strong: false };
  return { label: "Fair", strong: false };
}

/**
 * Whether the Trade Match surface should be queryable for this friend at all.
 * A null/non-finite friend id (seed or demo route) means there is no live
 * account to fetch, so the surface renders its empty state rather than
 * inventing trades.
 */
export function canQueryTradeMatches(friendId: number | null): boolean {
  return friendId != null && Number.isFinite(friendId);
}
